import React from 'react';
import {
  Box,
  IconButton,
  Typography,
  Card,
  CardContent,
  Chip
} from '@mui/material';
import { ChevronLeft, ChevronRight, Calendar } from 'lucide-react';
import { format, startOfWeek, endOfWeek, addWeeks, subWeeks, differenceInWeeks } from 'date-fns';

interface WeekNavigationProps {
  currentWeek: Date;
  onWeekChange: (week: Date) => void;
  title?: string;
}

export const WeekNavigation: React.FC<WeekNavigationProps> = ({ 
  currentWeek, 
  onWeekChange, 
  title = "Week Navigation" 
}) => {
  const weekStart = startOfWeek(currentWeek, { weekStartsOn: 1 }); // Monday start
  const weekEnd = endOfWeek(currentWeek, { weekStartsOn: 1 });
  const today = new Date();
  const thisWeekStart = startOfWeek(today, { weekStartsOn: 1 });
  
  const isCurrentWeek = differenceInWeeks(weekStart, thisWeekStart) === 0;
  const weeksFromNow = differenceInWeeks(weekStart, thisWeekStart);

  const handlePreviousWeek = () => {
    onWeekChange(subWeeks(currentWeek, 1));
  };

  const handleNextWeek = () => {
    onWeekChange(addWeeks(currentWeek, 1));
  };

  const handleCurrentWeek = () => {
    onWeekChange(today);
  };

  const getWeekLabel = () => {
    if (isCurrentWeek) return "This Week";
    if (weeksFromNow === -1) return "Last Week";
    if (weeksFromNow === 1) return "Next Week";
    if (weeksFromNow < 0) return `${Math.abs(weeksFromNow)} weeks ago`;
    if (weeksFromNow > 0) return `In ${weeksFromNow} weeks`;
    return "Unknown";
  };

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent sx={{ py: 2 }}>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={2}>
            <Calendar size={20} color="#6366f1" />
            <Typography variant="h6">{title}</Typography>
          </Box>
          
          <Box display="flex" alignItems="center" gap={1}>
            <IconButton onClick={handlePreviousWeek} size="small">
              <ChevronLeft size={20} />
            </IconButton>
            
            <Box textAlign="center" minWidth={200}>
              <Typography variant="subtitle1" fontWeight="bold">
                {format(weekStart, 'MMM d')} - {format(weekEnd, 'MMM d, yyyy')}
              </Typography>
              <Box display="flex" justifyContent="center" gap={1} mt={1}>
                <Chip 
                  size="small" 
                  label={getWeekLabel()}
                  color={isCurrentWeek ? "primary" : "default"}
                  variant={isCurrentWeek ? "filled" : "outlined"}
                />
                {!isCurrentWeek && (
                  <Chip 
                    size="small" 
                    label="Go to Current Week"
                    color="primary"
                    variant="outlined"
                    onClick={handleCurrentWeek}
                    sx={{ cursor: 'pointer' }}
                  />
                )}
              </Box>
            </Box>
            
            <IconButton 
              onClick={handleNextWeek} 
              size="small"
              disabled={weeksFromNow >= 4} // Prevent going too far into future
            >
              <ChevronRight size={20} />
            </IconButton>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};